/** Pure tempo + key detection on mono samples (runs in a worker): coarse tempo, then a beat grid fitted to the whole song. */
import { detectKey } from "./keyDetection";
import { detectTempo, onsetEnvelope } from "./tempoDetection";

const r3 = (v) => Math.round(v * 1000) / 1000;
const wrap = (v, p) => ((v % p) + p) % p;
const asBuffer = (data, sr) => ({ sampleRate: sr, length: data.length, duration: data.length / sr, numberOfChannels: 1, getChannelData: () => data });

/** Mean onset strength along a comb of `fpb` frames per beat starting at frame `ph` (interpolated between frames). */
function combAt(env, fpb, ph) {
  let s = 0;
  let n = 0;
  for (let f = ph; f < env.length - 1; f += fpb) {
    const i = Math.floor(f);
    s += env[i] + (env[i + 1] - env[i]) * (f - i);
    n += 1;
  }
  return n ? s / n : 0;
}

/** Search tempos lo..hi (step BPM) and phases (phaseStep frames) for the grid the onsets fit best. */
function bestGrid(env, hopSec, lo, hi, step, phaseStep) {
  let best = { bpm: lo, score: -1, phase: 0 };
  for (let bpm = lo; bpm <= hi; bpm += step) {
    const fpb = 60 / bpm / hopSec;
    for (let ph = 0; ph < fpb; ph += phaseStep) {
      const score = combAt(env, fpb, ph);
      if (score > best.score) best = { bpm, score, phase: ph };
    }
  }
  return best;
}

/** → { bpm, offset (first beat, s), tonic, mode, source: "auto", confidence } — the beat grid is offset + n·60/bpm. */
export function analyzeSamples(data, sr) {
  const dur = data.length / sr;
  const from = dur > 100 ? Math.round(Math.min(30, dur * 0.15) * sr) : 0;
  const coarse = detectTempo(asBuffer(data.subarray(from, from + 60 * sr), sr));
  const key = detectKey(asBuffer(data.subarray(from, from + 120 * sr), sr));
  const { env: raw, hopSec } = onsetEnvelope(asBuffer(data, sr));
  const mean = raw.reduce((a, v) => a + v, 0) / Math.max(1, raw.length);
  const env = raw.map((v) => Math.max(0, v - mean));
  const rough = bestGrid(env, hopSec, coarse.bpm * 0.96, coarse.bpm * 1.04, 0.05, 0.5);
  const best = bestGrid(env, hopSec, rough.bpm - 0.1, rough.bpm + 0.1, 0.005, 0.25);
  const bpm = Math.round(Math.max(40, Math.min(240, best.bpm)) * 100) / 100;
  return {
    bpm, offset: r3(wrap((best.phase + 1) * hopSec, 60 / bpm)), tonic: key.tonic, mode: key.mode, source: "auto",
    confidence: r3(Math.max(0, Math.min(1, coarse.confidence, key.confidence))),
  };
}
